import { Component, HostBinding, Input, inject } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CommonModule, NgIf } from '@angular/common';
import { RippleModule } from 'primeng/ripple';
import { MenuItem } from 'primeng/api';
import { LayoutService } from '../../../app/service/layout-service/layout.service';
import { Menu } from 'primeng/menu';
import { ButtonModule } from 'primeng/button';
import { TemplateService } from '../../../app/service/template-service/template.service';
import { ToastService } from '../../../app/service/toast-service/toast.service';

@Component({
  selector: 'app-menuitem',
  imports: [CommonModule, RouterModule, RippleModule, NgIf, ButtonModule, Menu],
  templateUrl: './app-menuitem.component.html',
  styleUrl: './app-menuitem.component.scss',
  animations: [
    trigger('children', [
        state(
            'collapsed',
            style({
                height: '0'
            })
        ),
        state(
            'expanded',
            style({
                height: '*'
            })
        ),
        transition('collapsed <=> expanded', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'))
    ])
  ],
  providers: [LayoutService]
})
export class AppMenuitemComponent {
    actionItems: MenuItem[] | undefined;
  @Input() item!: MenuItem;

  @Input() index!: number;

  @Input() @HostBinding('class.layout-root-menuitem') root!: boolean;

  @Input() parentKey!: string;

  active = false;

  menuSourceSubscription: Subscription;

  menuResetSubscription: Subscription;

  key: string = '';

  private router = inject(Router);
  private templateService = inject(TemplateService);
  private toastService = inject(ToastService);

  constructor(
      private layoutService: LayoutService
  ) {
      this.menuSourceSubscription = this.layoutService.menuSource$.subscribe((value) => {
          Promise.resolve(null).then(() => {
              if (value.routeEvent) {
                  this.active = value.key === this.key || value.key.startsWith(this.key + '-') ? true : false;
              } else {
                  if (value.key !== this.key && !value.key.startsWith(this.key + '-')) {
                      this.active = false;
                  }
              }
          });
      });

      this.menuResetSubscription = this.layoutService.resetSource$.subscribe(() => {
          this.active = false;
      });

      this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((params) => {
          if (this.item.routerLink) {
              this.updateActiveStateFromRoute();
          }
      });
  }

  ngOnInit() {
      this.key = this.parentKey ? this.parentKey + '-' + this.index : String(this.index);

      if (this.item.routerLink) {
          this.updateActiveStateFromRoute();
      }

      this.actionItems = [
            {
                items: [
                    {
                        label: 'Edit',
                        icon: 'pi pi-pencil',
                        command: () => {
                            this.editTemplate(this.item['templateId']);
                        }
                    },
                    {
                        label: 'Duplicate',
                        icon: 'pi pi-copy',
                        command: () => {
                            this.duplicateTemplate(this.item['templateId']);
                        }
                    }
                ]
            }
        ];
  }

  private editTemplate(templateId: string): void {
    if (templateId) {
        this.router.navigate(['/template/edit', templateId]);
    }
  }

  private duplicateTemplate(templateId: string): void {
    if (templateId) {
        this.templateService.duplicate(templateId).subscribe({
            next: (newTemplate) => {
                this.toastService.success('Template duplicated successfully');
                // Navigate to the new template editor page
                this.router.navigate(['/template/edit', newTemplate.id]);
            },
            error: (error) => {
                this.toastService.error('Failed to duplicate template');
                console.error('Error duplicating template:', error);
            }
        });
    }
  }

  updateActiveStateFromRoute() {
      let activeRoute = this.router.isActive(this.item.routerLink[0], { paths: 'exact', queryParams: 'exact', matrixParams: 'ignored', fragment: 'ignored' });

      if (activeRoute) {
          this.layoutService.onMenuStateChange({ key: this.key, routeEvent: true });
      }
  }

  itemClick(event: Event) {
      // avoid processing disabled items
      if (this.item.disabled) {
          event.preventDefault();
          return;
      }

      // execute command
      if (this.item.command) {
          this.item.command({ originalEvent: event, item: this.item });
      }

      // toggle active state
      if (this.item.items) {
          this.active = !this.active;
      }

      this.layoutService.onMenuStateChange({ key: this.key });
  }

  get submenuAnimation() {
      return this.root ? 'expanded' : this.active ? 'expanded' : 'collapsed';
  }

  @HostBinding('class.active-menuitem')
  get activeClass() {
      return this.active && !this.root;
  }

  ngOnDestroy() {
      if (this.menuSourceSubscription) {
          this.menuSourceSubscription.unsubscribe();
      }

      if (this.menuResetSubscription) {
          this.menuResetSubscription.unsubscribe();
      }
  }

  isPrimeIcon(icon: string): boolean {
    return typeof icon === 'string' && icon.startsWith('pi ');
  }

  isMaterialIcon(icon: string): boolean {
    return typeof icon === 'string' && icon.startsWith('material:');
  }

  isMaterialSymbolsIcon(icon: string): boolean {
    return typeof icon === 'string' && icon.startsWith('symbols:');
  }

  getMaterialIconName(icon: string): string {
    return icon.split(':')[1] || '';
  }
}
